/**
 * White-Label Configuration — custom branding for coaches and gym owners.
 *
 * Levels:
 * - Coach: custom accent color + logo on client-facing screens
 * - Club: custom TV mode branding (already partially built)
 * - Enterprise: full white-label (custom app name, icon, colors)
 *
 * Revenue: $20/mo add-on for coaches, $100/mo for clubs, enterprise pricing for full white-label.
 */

import { supabase } from './supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from './logger';

const log = createLogger('WHITE_LABEL');
const CACHE_KEY = 'duel:white_label';

export interface BrandConfig {
  logoUrl: string | null;
  accentColor: string; // hex color
  appName: string;
  tagline: string | null;
  primaryFont: string | null;
  hideDefaultBranding: boolean; // hide "DUEL" branding
}

const DEFAULT_BRAND: BrandConfig = {
  logoUrl: null,
  accentColor: '#F59E0B', // DUEL gold
  appName: 'DUEL',
  tagline: 'Do U Even Lift',
  primaryFont: null,
  hideDefaultBranding: false,
};

/**
 * Get brand config for a gym or coach.
 * Returns custom branding if set, otherwise default DUEL branding.
 */
export async function getBrandConfig(orgId?: string): Promise<BrandConfig> {
  // Check local cache first
  try {
    const cached = await AsyncStorage.getItem(CACHE_KEY);
    if (cached) {
      const config = JSON.parse(cached);
      if (config._orgId === orgId && Date.now() - config._ts < 300000) { // 5 min cache
        return config;
      }
    }
  } catch {}

  if (!orgId) return DEFAULT_BRAND;

  try {
    const { data } = await supabase
      .from('gyms')
      .select('name, logo_url, accent_color')
      .eq('id', orgId)
      .maybeSingle();

    if (!data) return DEFAULT_BRAND;

    const config: BrandConfig = {
      logoUrl: data.logo_url ?? null,
      accentColor: data.accent_color ?? DEFAULT_BRAND.accentColor,
      appName: data.name ?? DEFAULT_BRAND.appName,
      tagline: null,
      primaryFont: null,
      hideDefaultBranding: !!data.logo_url, // hide DUEL branding if custom logo set
    };

    // Cache it
    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ ...config, _orgId: orgId, _ts: Date.now() }));
    return config;
  } catch (err) {
    log.warn('getBrandConfig failed:', err);
    return DEFAULT_BRAND;
  }
}

/**
 * Save brand config for a gym.
 */
export async function saveBrandConfig(
  gymId: string,
  config: Partial<Pick<BrandConfig, 'logoUrl' | 'accentColor'>>,
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('gyms')
      .update({
        logo_url: config.logoUrl,
        accent_color: config.accentColor,
      })
      .eq('id', gymId);

    if (error) {
      log.error('saveBrandConfig failed:', error.message);
      return false;
    }

    // Clear cache
    await AsyncStorage.removeItem(CACHE_KEY);
    return true;
  } catch {
    return false;
  }
}
